import type { ReactNode } from 'react';

/**
 * Base skeleton component with shimmer animation
 */
export function Skeleton({
  className = '',
  children,
}: {
  className?: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}>
      {children}
    </div>
  );
}

/**
 * Text line skeleton with customizable width
 */
export function SkeletonText({
  width = 'w-full',
  size = 'md',
}: {
  width?: string;
  size?: 'sm' | 'md' | 'lg';
}): ReactNode {
  const sizeClasses = {
    sm: 'h-3',
    md: 'h-4',
    lg: 'h-5',
  };

  return <Skeleton className={`${sizeClasses[size]} ${width}`} />;
}

/**
 * Card skeleton for stat cards and similar components
 */
export function SkeletonCard({
  className = '',
}: {
  className?: string;
}): ReactNode {
  return (
    <div className={`rounded-lg border bg-white p-6 ${className}`}>
      <SkeletonText width="w-24" size="sm" />
      <div className="mt-4">
        <SkeletonText width="w-20" size="lg" />
      </div>
    </div>
  );
}

/**
 * Table row skeleton
 */
export function SkeletonTableRow({
  columns = 4,
}: {
  columns?: number;
}): ReactNode {
  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <SkeletonText width={i === 0 ? 'w-3/4' : 'w-1/2'} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Table skeleton with header and rows
 */
export function SkeletonTable({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}): ReactNode {
  return (
    <div className="overflow-hidden rounded-lg border bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <SkeletonText width="w-16" size="sm" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Grid of card skeletons
 */
export function SkeletonCardGrid({
  count = 4,
  columns = 4,
}: {
  count?: number;
  columns?: 2 | 3 | 4;
}): ReactNode {
  const gridClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 ${gridClasses[columns]}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * List item skeleton
 */
export function SkeletonListItem(): ReactNode {
  return (
    <div className="rounded-lg border bg-white p-6">
      <SkeletonText width="w-3/4" />
      <div className="mt-4">
        <SkeletonText width="w-1/2" size="sm" />
      </div>
    </div>
  );
}

/**
 * List skeleton
 */
export function SkeletonList({
  count = 5,
}: {
  count?: number;
}): ReactNode {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  );
}

/**
 * Page header skeleton
 */
export function SkeletonPageHeader(): ReactNode {
  return (
    <div className="animate-pulse">
      <div className="h-7 bg-gray-200 rounded w-48"></div>
      <div className="mt-2 h-4 bg-gray-200 rounded w-64"></div>
    </div>
  );
}

/**
 * Stat grid skeleton for colored stat cards
 */
export function SkeletonStatGrid({
  count = 4,
}: {
  count?: number;
}): ReactNode {
  const colors = [
    { bg: 'bg-blue-50', inner: 'bg-blue-100' },
    { bg: 'bg-green-50', inner: 'bg-green-100' },
    { bg: 'bg-purple-50', inner: 'bg-purple-100' },
    { bg: 'bg-orange-50', inner: 'bg-orange-100' },
  ];

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 animate-pulse">
      {Array.from({ length: count }).map((_, i) => {
        const colorIndex = i % colors.length;
        const colorSet = colors[colorIndex];
        if (!colorSet) return null;
        return (
          <div key={i} className={`rounded-lg p-6 ${colorSet.bg}`}>
            <div className={`h-3 w-24 rounded ${colorSet.inner}`}></div>
            <div className={`mt-2 h-9 w-16 rounded ${colorSet.inner}`}></div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Dashboard stats skeleton (4 cards in a row)
 */
export function SkeletonDashboardStats(): ReactNode {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-lg border bg-white p-6 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-5 w-5 bg-gray-200 rounded"></div>
          </div>
          <div className="mt-2 h-8 bg-gray-200 rounded w-20"></div>
        </div>
      ))}
    </div>
  );
}

/**
 * Billing page skeleton
 */
export function SkeletonBillingPage(): ReactNode {
  return (
    <div className="space-y-8 animate-pulse">
      {/* Header */}
      <div>
        <div className="h-7 bg-gray-200 rounded w-56"></div>
        <div className="mt-2 h-4 bg-gray-200 rounded w-72"></div>
      </div>

      {/* Current Plan */}
      <div className="rounded-lg border bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-5 bg-gray-200 rounded w-32"></div>
            <div className="flex items-center gap-3">
              <div className="h-7 w-20 bg-gray-200 rounded-full"></div>
              <div className="h-4 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>

      {/* Usage */}
      <div className="rounded-lg border bg-white p-6">
        <div className="h-5 bg-gray-200 rounded w-32"></div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between">
                <div className="h-3 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full w-full"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Plans grid */}
      <div>
        <div className="h-5 bg-gray-200 rounded w-40"></div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-40"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
              <div className="mt-6 space-y-3">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="flex items-center gap-2">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 h-10 bg-gray-200 rounded-lg w-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Admin overview skeleton
 */
export function SkeletonAdminOverview(): ReactNode {
  return (
    <div className="animate-pulse">
      <div className="h-7 bg-gray-200 rounded w-40 mb-8"></div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[
          { bg: 'bg-blue-100', text: 'bg-blue-200' },
          { bg: 'bg-green-100', text: 'bg-green-200' },
          { bg: 'bg-purple-100', text: 'bg-purple-200' },
          { bg: 'bg-orange-100', text: 'bg-orange-200' },
        ].map((colors, i) => (
          <div key={i} className={`rounded-lg p-6 ${colors.bg}`}>
            <div className={`h-3 w-24 rounded ${colors.text}`}></div>
            <div className={`mt-2 h-9 w-16 rounded ${colors.text}`}></div>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-lg bg-white p-6 shadow">
        <div className="h-5 bg-gray-200 rounded w-32 mb-4"></div>
        <div className="flex gap-4">
          <div className="h-10 w-36 bg-gray-200 rounded-lg"></div>
          <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>
  );
}
